import { useState, useEffect } from 'react';
import { planPricingService, PricingTier, PlanMetadata } from '../services/planPricingService';
import type { ProAnnualPricingTier } from '../config/proPlanPricing';
import { getProAnnualPrice } from '../config/proPlanPricing';

interface UsePlanPricingReturn {
  proPricing: PricingTier[];
  proAnnualPricing: ProAnnualPricingTier[];
  businessPricing: PricingTier[];
  starterMetadata: PlanMetadata | null;
  proMetadata: PlanMetadata | null;
  businessMetadata: PlanMetadata | null;
  isLoading: boolean;
  getPrice: (plan: 'pro' | 'business', subscribers: number) => Promise<number>;
  getProAnnualPrice: (subscribers: number) => number;
  refresh: () => Promise<void>;
}

export const usePlanPricing = (): UsePlanPricingReturn => {
  const [proPricing, setProPricing] = useState<PricingTier[]>([]);
  const [proAnnualPricing, setProAnnualPricing] = useState<ProAnnualPricingTier[]>([]);
  const [businessPricing, setBusinessPricing] = useState<PricingTier[]>([]);
  const [starterMetadata, setStarterMetadata] = useState<PlanMetadata | null>(null);
  const [proMetadata, setProMetadata] = useState<PlanMetadata | null>(null);
  const [businessMetadata, setBusinessMetadata] = useState<PlanMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadPricing = async () => {
    setIsLoading(true);
    try {
      const pricing = await planPricingService.getPlanPricing();
      setProPricing(pricing.pro.tiers);
      setProAnnualPricing(pricing.pro.annualTiers || []);
      setBusinessPricing(pricing.business.tiers);
      setStarterMetadata(pricing.starter.metadata);
      setProMetadata(pricing.pro.metadata);
      setBusinessMetadata(pricing.business.metadata);
    } catch (error) {
      console.error('Error loading plan pricing:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPricing();
  }, []);

  const getPrice = async (plan: 'pro' | 'business', subscribers: number): Promise<number> => {
    return await planPricingService.getPriceForSubscribers(plan, subscribers);
  };

  const getProAnnualPriceForSubscribers = (subscribers: number): number =>
    getProAnnualPrice(subscribers, proAnnualPricing.length > 0 ? proAnnualPricing : undefined);

  return {
    proPricing,
    proAnnualPricing,
    businessPricing,
    starterMetadata,
    proMetadata,
    businessMetadata,
    isLoading,
    getPrice,
    getProAnnualPrice: getProAnnualPriceForSubscribers,
    refresh: loadPricing
  };
};
